'use client';

import { Box, Button, Grid, Typography, useTheme } from '@mui/material';
import Link from 'next/link';
import React from 'react';

import GlobalBlockRenderer, {
  prefixClasses,
  processCustomClassesFromText,
} from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiImage } from '@/components/Utility/StrapiImage';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksStepItem,
  ComponentBlocksStepsList,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

export function StepsListPlain({ blocks }: { blocks: ComponentBlocksStepsList }) {
  const theme = useTheme();

  const steps = (blocks?.Steps ?? []).filter(
    (step): step is ComponentBlocksStepItem => step != null
  );

  if (!steps.length) return null;

  return (
    <CustomBackground
      backgroundOptions={(blocks?.StepsListBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        (blocks?.StepsListDisplayOptions ?? {
          ContentsWidth: Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'sm',
        }) as StrapiDisplayOptions
      }
      boxProps={{
        // @ts-expect-error - this does work, but types are not correct
        'data-component': 'StepsListPlain',
      }}
    >
      {(blocks?.Title || blocks?.StepsListBlurb) && (
        <Box
          sx={{
            mb: 2,
            maxWidth: 'sm',
            mx: 'auto',
            justifyItems: 'center',
            textAlign: 'center',
            '> *': {
              textAlign: 'center',
            },
          }}
        >
          {blocks?.Title && (
            <Typography
              variant="h2"
              textAlign="center"
              fontWeight={700}
              sx={{
                color: theme.palette.common.black,
                pb: { xs: 3, md: 4 },
                px: 2,
                maxWidth: '48rem',
                mx: 'auto',
                lineHeight: 1.25,
              }}
            >
              {blocks.Title}
            </Typography>
          )}
        </Box>
      )}

      {blocks?.StepsListBlurb && (
        <Box
          sx={{
            mb: 3,
          }}
        >
          <GlobalBlockRenderer content={blocks.StepsListBlurb || []} />
        </Box>
      )}

      <Grid container spacing={6}>
        {steps.map((step, index) => (
          <Grid item xs={12} md={6} key={index}>
            <StepItem
              step={step}
              index={index}
              StepsListShowNumbers={blocks.StepsListShowNumbers ?? false}
            />
          </Grid>
        ))}
      </Grid>
    </CustomBackground>
  );
}

function StepItem({
  step,
  index,
  StepsListShowNumbers,
}: {
  step: ComponentBlocksStepItem;
  index: number;
  StepsListShowNumbers: boolean | undefined;
}) {
  const theme = useTheme();
  const cta = step.StepItemCta;
  const hasCta = cta && cta.ShowButton && cta.ButtonText;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      {/* Step Number */}
      {StepsListShowNumbers === true && (
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.75rem', sm: '2rem' },
            color: theme.palette.common.black,
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          {index + 1}
        </Typography>
      )}

      <Box sx={{ minWidth: 0 }}>
        {/* Step Image */}
        {step?.Image && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              width: '80px',
              height: '80px',
              mx: 'auto',
              '& img': {
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              },
            }}
          >
            <StrapiImage image={step.Image} size="logo" imageWidth={80} imageHeight={80} />
          </Box>
        )}

        {/* Step Title */}
        {step?.Title &&
          (() => {
            const { classes, cleanedText } = processCustomClassesFromText(step.Title);
            return (
              <Typography
                variant="h5"
                className={classes.length > 0 ? prefixClasses(classes) : undefined}
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', sm: '1.6rem' },
                  lineHeight: 1.3,
                  mb: 1,
                  color: theme.palette.common.black,
                }}
              >
                {cleanedText}
              </Typography>
            );
          })()}

        {/* Step Description */}
        {step?.Description && (
          <Box sx={{ mb: hasCta ? 2 : 0 }}>
            <GlobalBlockRenderer
              content={step.Description}
              overrides={{
                blocks: {
                  paragraph: ({ children }: { children: React.ReactNode }) => (
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                        mb: 1.2,
                      }}
                    >
                      {children}
                    </Typography>
                  ),
                },
              }}
            />
          </Box>
        )}

        {/* CTA Button */}
        {hasCta && (
          <Box sx={{ mt: 1 }}>
            {cta.ButtonLink ? (
              cta.ButtonLink.startsWith('http') ? (
                <Button
                  variant="contained"
                  color="primary"
                  href={cta.ButtonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cta.ButtonText}
                </Button>
              ) : (
                <Button variant="contained" color="primary" component={Link} href={cta.ButtonLink}>
                  {cta.ButtonText}
                </Button>
              )
            ) : (
              <Button variant="contained" color="primary">
                {cta.ButtonText}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
